import React from "react";
import {
  GKButton,
  GKIconName,
  GKAutoComplete,
  GKHighlightText,
  GKIcon,
  Grouping,
  FlexibleNavItem,
  IGKAutoCompleteRef,
  GKFormGroup,
  GKLayoutShellContext,
  useDeviceSizes,
} from "@granular/gds-ux-components";
import { useHistory, useLocation } from "react-router-dom";
import { validate as uuidValidate } from "uuid";
import { Operation } from "../models";
import { getCoreTranslationsFor, getRegionFromUrl } from "../i18n";
import { operationsService } from "../services";
import { tripInsightsDataDirtyFlag } from "../services/insights";
import {
  getRegexMatchingFromUrl,
  getGranularRuntimeEnvName,
} from "../helpers/url";
import {
  NAV_NOTIFICATIONS_INSIGHTS_KEY,
  NAV_CROPVISOR_ENROLLED_FEATURE_ID,
} from "../constants";
import { LSRefreshKey } from "./Login";
import debounce from "lodash/debounce";
import { useGetUnreadReportsCount } from "@granular/report-hooks";
import { GranularCatalogConfig } from "@granular/fabric-catalog-helpers";
import { segmentService } from "../legacy/segment/SegmentService";

import {
  useAuthToken,
  useLocale,
  useAuthenticationData,
  useCurrentOperationId,
  useCurrentOperationData,
  useOperationsData,
  useMaybeEnrollmentsData,
  useFeatureFlag,
  useMaybeUser,
  useMaybeMembersData,
  useMaybeFabricFeatureFlag,
} from "../next/hooks";

type NavItem = {
  useOperation: boolean;
  label: string;
  appPath: string;
  notifications?: number | string;
  notificationBadgeColor?: string;
  operationPath?: string;
  isEnabled: boolean;
  icon?: GKIconName;
  qaId?: string;
  customRoute?: boolean;
  analyticsKey?: string;
  analyticsMetadata?: object;
  href?: string;
};

// Should match the type in the link below
// https://gitlab.internal.granular.ag/granular-insights/insights-ui/-/blob/master/src/shared/services/persistence/insightsViewed/insightsViewed.helpers.ts#L11
type InsightsNotifications = {
  harvestMoisture: boolean;
  plantingDate: boolean;
  yieldPrediction: boolean;
  cashBids: boolean;
  varietyComparison: boolean;
};

const primaryQaPrefix = "primary-nav-item";
const secondaryQaPrefix = "secondary-nav-item";

const selectCanViewOpSettings = (currentOperation: Operation) =>
  currentOperation.permissions.isAdmin ||
  currentOperation.permissions.isAdvisor;

/**
 * NOTE: This component is maintained by Team CAT.
 *
 * TODO: Split this component and its tests out. It's time.
 *
 * The Operations dropdown. It shows up in the navigation bar and can be
 * embedded either in `GKResponsiveNav` or `GKMainNav`. It wraps the
 * `GKAutoComplete` component so people can search through a list of
 * operations.
 *
 * As of this writing, the *average* number of operations per customer is 3,
 * with a 99th-percentile of 37. This is not bad and the `GKAutoComplete`
 * component can search this list (or lists around 3x that size) easily with
 * few performance concerns.
 *
 * However, Superusers can and CSAs may have very large numbers of operations
 * associated with them. E.g. one CSA somehow has 16,000 operations! Superusers
 * are at the extreme end: we need a way for them to search through _all_
 * 370,000+ operations (in North America.) Rendering all these and deferring to
 * the `GKAutoComplete` component to search through them is a nightmare.
 *
 * So we have a simple fuzzy-seach endpoint in the Roles Service. We show only
 * the first 100 operations to any user. This takes care of 99%+ of our
 * customers and CSAs. If the user has a 100 operations or fewer, we just use
 * `GKAutoComplete` to search locally.
 *
 * If they have _more_ than 100 operations, we call the Roles Service and
 * display the results. No hooks, no additional machinery required. Simple,
 * debounced Promise-calling, that's all 💗
 */
export const OperationDropdown: React.FC<{ translateRoutes?: boolean }> = ({
  translateRoutes,
}) => {
  const currentOperation = useCurrentOperationData();
  const locale = useLocale();
  const messages = getCoreTranslationsFor(locale);
  const { operations, totalOperationsCount } = useOperationsData();
  const paths = location.pathname.split(
    getRegexMatchingFromUrl(
      translateRoutes ? messages.routing_operations : "operations",
    ),
  );
  const [opData, setOpData] = React.useState<Grouping<Operation>[]>([
    new Grouping(messages.common_operationsControls_myOperations, operations),
  ]);
  const history = useHistory();
  const OperationDropdownRef = React.useRef<IGKAutoCompleteRef>(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "roles-api",
  });
  const shellContext = React.useContext(GKLayoutShellContext);
  const { isMobileSized } = useDeviceSizes();

  const currentOperationId = currentOperation.id;
  const doWeHaveAllOperations = operations.length === totalOperationsCount;

  const pathsAt1 = paths[1];
  const urlOperationId = React.useMemo(
    () => (pathsAt1 && uuidValidate(pathsAt1) ? pathsAt1 : undefined),
    [pathsAt1],
  );

  const filterFunction = React.useCallback(
    (operation: Operation, filter: string): boolean =>
      operation.name.toLowerCase().includes(filter.toLowerCase()),
    [],
  );

  const itemTemplate = React.useCallback(
    (operation: Operation, filter: string) => (
      <div className="w-100">
        <GKHighlightText match={filter}>{operation.name}</GKHighlightText>
      </div>
    ),
    [],
  );

  const addOperationClick = React.useCallback(() => {
    OperationDropdownRef.current?.close();
    if (isMobileSized) {
      shellContext.setNavExpanded(false);
    }
    history.push("/settings/operations/add");
  }, [history, isMobileSized, shellContext]);

  const onSelection = React.useCallback(
    (operation: Operation): void => {
      if (operation.id === currentOperationId) {
        return;
      }

      const operationRoute = translateRoutes
        ? messages.routing_operations
        : "operations";
      const paths = location.pathname.split(
        getRegexMatchingFromUrl(operationRoute),
      );
      const firstChild = paths[2]?.split("/")?.[1];
      const operationChild = firstChild ? `/${firstChild}` : "";
      let newPath = `${paths[0]}/${operationRoute}/${operation.id}${operationChild}`;

      // FIXME: Temporary fix for https://granular.atlassian.net/browse/APLO-2047, which is to avoid the erroneous side effects happening due to the very quick double redirect, by directing the user to the end result of the redirect in the first place. This is a temporary solution because Core should not have to know about the redirecting going on by individual applications. Unfortunately, the full, correct solution is unknown at this time because the full cause is unknown at this time. See the description and comments on https://granular.atlassian.net/browse/APLO-2047 for more details.
      const secondChild = paths[2]?.split("/")?.[2];
      if (firstChild === "insights" && secondChild) {
        newPath = `${newPath}/${secondChild}`;
      }

      history.push(newPath);
      tripInsightsDataDirtyFlag();
    },
    [history, translateRoutes, currentOperationId, messages.routing_operations],
  );

  const footerTemplate = React.useCallback(
    () => (
      <GKButton
        color="link"
        className="w-100 text-decoration-none"
        data-qa="operations-autocomplete-add-operation-button"
        onClick={addOperationClick}
      >
        <div className="d-flex justify-content-between align-items-center">
          {messages.add_operation}
          <GKIcon name={GKIconName.Add} />
        </div>
      </GKButton>
    ),
    [messages.add_operation, addOperationClick],
  );

  const handleChange = React.useCallback(async (e: any) => {
    setSearchTerm(e.target.value);
  }, []);

  const debouncedHandleChange = React.useMemo(
    () =>
      debounce(
        handleChange,
        /**
         * 250ms is the median human response time. Roles Service takes ~150ms on
         * average to fulfill the search request.
         */
        250,
      ),
    [handleChange],
  );

  React.useEffect(() => {
    (async () => {
      let operationsToShow = operations;

      if (!doWeHaveAllOperations && searchTerm.trim() !== "") {
        // TODO: Refactor this to a React Query hook.
        // This will allow us to revalidate or clear this data when the operations
        // are revalidated or cleared. This will also allow us to clean this up and
        // move much of this state management out of this component.
        operationsToShow = await operationsService.searchForOperations(
          searchTerm,
          catalogFlag,
        );
      }

      setOpData([
        new Grouping(
          messages.common_operationsControls_myOperations,
          operationsToShow,
        ),
      ]);
    })();
  }, [
    messages.common_operationsControls_myOperations,
    operations,
    searchTerm,
    doWeHaveAllOperations,
    catalogFlag,
  ]);

  const onBlur = React.useCallback(() => {
    try {
      // unselect all text on blur
      window.getSelection()?.removeAllRanges();
    } catch (error) {
      // do nothing
    }
  }, []);

  return (
    <GKFormGroup className="mb-0">
      <GKAutoComplete
        data-qa="operations-autocomplete"
        data={opData}
        disabled={!urlOperationId}
        onBlur={onBlur}
        filterFunction={
          // Don't use the filter function if we're calling upstream for search
          !doWeHaveAllOperations ? undefined : filterFunction
        }
        footerTemplate={footerTemplate}
        icon={GKIconName.Search}
        itemTemplate={itemTemplate}
        itemToString={(operation): string => operation.name}
        onSelection={onSelection}
        onChange={
          // Use a change handler only if we're calling upstream for search
          !doWeHaveAllOperations ? debouncedHandleChange : undefined
        }
        /**
         * Restore the search term when the user clicks the dropdown again.
         * This will also restore the operations list.
         */
        onFocus={() => {
          setSearchTerm("");
        }}
        placeholder={messages.select_operation}
        ref={OperationDropdownRef}
        selectedItem={currentOperation}
      />
    </GKFormGroup>
  );
};

// A hook to return the current number of insights notifications.
export const useInsightsNotificationCount = (): number => {
  const [insightsNotifications, setInsightsNotifications] = React.useState(0);

  const flagForEnableYieldPredictionInsight = useFeatureFlag({
    app: "g_insights",
    key: "enable_yield_prediction_insight",
  });
  const flagForEnableVarietyComparisonInsight = useFeatureFlag({
    app: "g_insights",
    key: "enable_variety_comparison_insight",
  });

  const getInsightsLength = () => {
    const activeInsights = ["plantingDate", "harvestMoisture"];
    const isYieldPredictEnabled = flagForEnableYieldPredictionInsight;
    const isVarietyComparisonEnabled = flagForEnableVarietyComparisonInsight;

    if (isYieldPredictEnabled) {
      activeInsights.push("yieldPrediction");
    }
    if (isVarietyComparisonEnabled) {
      activeInsights.push("varietyComparison");
    }

    try {
      const ls = window.localStorage.getItem(NAV_NOTIFICATIONS_INSIGHTS_KEY);
      if (!ls) {
        return activeInsights.length;
      }
      const lsObject: InsightsNotifications = JSON.parse(ls);
      return Object.keys(lsObject).filter(
        (i) => activeInsights.includes(i) && lsObject[i] === false,
      ).length;
    } catch (_e) {
      return activeInsights.length;
    }
  };

  React.useEffect(() => {
    const notificationsEventListener = () => {
      setInsightsNotifications(getInsightsLength());
    };
    notificationsEventListener();
    window.addEventListener("storage", notificationsEventListener);
    return () => {
      window.removeEventListener("storage", notificationsEventListener);
    };
  });

  return insightsNotifications;
};

// Returns the number of report nofications from document-service
const useReportNotificationsCount = (): number => {
  const reportsFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-reports",
  });
  const { pathname } = useLocation();
  const authToken = useAuthToken();
  const locale = useLocale();
  const currentOperationId = useCurrentOperationId();
  const granularCatalogConfig: GranularCatalogConfig = React.useMemo(
    () => ({
      getAuthToken: () => authToken,
      getOperationId: () => currentOperationId,
      getLocale: () => locale,
    }),
    [authToken, currentOperationId, locale],
  );
  return useGetUnreadReportsCount(granularCatalogConfig, pathname, undefined, {
    enabled: reportsFlag,
  });
};

// Returns an array of nav items that can be used in
// GKMainNav (via GKLayoutShell).
export const usePrimaryNavItems = (): FlexibleNavItem[] => {
  const history = useHistory();
  const locale = useLocale();
  const currentOperationId = useCurrentOperationId();
  const enrollments = useMaybeEnrollmentsData();
  const messages = getCoreTranslationsFor(locale);
  const insightsAppPath = "/app";
  const insightsNotifications = useInsightsNotificationCount();
  const reportsNotifications = useReportNotificationsCount();

  const analysisFlag = useFeatureFlag({
    app: "g_insights",
    key: "enable_analysis",
  });
  const analyzeFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-analyze",
  });
  const activitiesFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-activities",
  });
  const carbonFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-carbon",
  });
  const reportsFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-reports",
  });
  const cropvisorFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-cropvisor",
  });
  const cropvisorRetailerFlag = useFeatureFlag({
    app: "fabric",
    key: "spraytiming-is-retailer",
  });
  const labsFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-labs",
  });
  const reportsNotificationsFlag = useFeatureFlag({
    app: "document-service",
    key: "enable-reports-notification-nav-badge",
  });
  const workspacesFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-workspaces",
  });
  const canOptIntoAnalyze = useFeatureFlag({
    app: "g_insights",
    key: "enable-analyze-opt-in",
  });
  const seedAdvisorFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-seed-advisor",
  });
  const nreFlag = useFeatureFlag({
    app: "fabric",
    key: "repexp-show-optin-link",
  });
  const shouldShowAnalyzeTryBetaBadge =
    canOptIntoAnalyze &&
    // Only show badge in english due to badge size constraints (french translation for "Try Beta" is too long to fit in the nav)
    // Additionally, this badge will go away when the beta period ends
    locale.startsWith("en-");

  const { data: maybeUser } = useMaybeUser();
  const showAnalyze = Boolean(
    analyzeFlag && maybeUser?.preferences.analyzeBetaOptIn,
  );
  const mapLayersFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-map-layers",
  });

  const buildNavLink = React.useCallback(
    (i: NavItem) => {
      if (!i.useOperation) {
        return i.appPath;
      }
      return `${i.appPath}/operations/${currentOperationId}${
        i.operationPath || ""
      }`;
    },
    [currentOperationId],
  );

  const canUseCropvisor = Boolean(
    cropvisorFlag &&
      (enrollments?.enrollments.some(
        (e) => e.featureId === NAV_CROPVISOR_ENROLLED_FEATURE_ID,
      ) ||
        cropvisorRetailerFlag),
  );

  const getEnvName = (): string => {
    return getGranularRuntimeEnvName() === "granappdevelopment" ? "dev." : "";
  };

  const getNreUrl = (): string => {
    const shortEnv = getEnvName();
    return `https://us.${shortEnv}rep.granular.ag/`;
  };

  const navItems: NavItem[] = React.useMemo(
    () => [
      {
        useOperation: false,
        label: messages.open_new_insights,
        icon: GKIconName.OpenInNew,
        appPath: insightsAppPath,
        isEnabled: nreFlag,
        qaId: "nre",
        href: getNreUrl(),
      },
      {
        useOperation: true,
        label: messages.map,
        icon: GKIconName.Map,
        appPath: insightsAppPath,
        operationPath: "/fields",
        isEnabled: true,
        qaId: "map",
        customRoute: true,
      },
      {
        useOperation: true,
        label: messages.Map_Layers,
        icon: GKIconName.MapLayer,
        appPath: "/map-layers",
        isEnabled: mapLayersFlag,
        qaId: "maplayers",
        analyticsKey: "Map Layers Nav Button Clicked",
        analyticsMetadata: {
          device: window.navigator.userAgent,
          screen: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
      },
      {
        useOperation: true,
        label: messages.monitoring_activities,
        icon: GKIconName.TaskOutline,
        appPath: "/activities",
        isEnabled: activitiesFlag,
        qaId: "activities",
      },
      {
        useOperation: true,
        label: messages.VRS,
        icon: GKIconName.Planting,
        appPath: "/workspaces/vrs",
        isEnabled: workspacesFlag,
        qaId: "workspaces",
        analyticsKey: "VRS Button Clicked",
        analyticsMetadata: {
          device: window.navigator.userAgent,
          screen: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
        },
      },
      {
        useOperation: true,
        label: "Proposals",
        icon: GKIconName.CornYield,
        appPath: "/seed-proposals",
        isEnabled: seedAdvisorFlag,
        qaId: "seedAdvisor",
      },
      {
        useOperation: true,
        label: messages.router_summary,
        icon: GKIconName.Summary,
        appPath: insightsAppPath,
        operationPath: "/summary",
        isEnabled: analysisFlag && !showAnalyze,
        qaId: "summary",
        customRoute: true,
        notifications: shouldShowAnalyzeTryBetaBadge
          ? messages.beta_nav_badge
          : undefined,
        notificationBadgeColor: shouldShowAnalyzeTryBetaBadge
          ? "info"
          : undefined,
      },
      {
        useOperation: true,
        label: messages.router_analyze,
        icon: GKIconName.BarChart,
        appPath: "/analyze",
        isEnabled: analyzeFlag && showAnalyze,
        analyticsKey: "Analyze Nav Button Clicked",
        qaId: "analyze",
      },
      {
        useOperation: true,
        label: messages.router_insights,
        icon: GKIconName.Timeline,
        appPath: insightsAppPath,
        notifications: insightsNotifications || undefined,
        operationPath: "/insights",
        isEnabled: analysisFlag,
        qaId: "insights",
        customRoute: true,
      },
      {
        useOperation: true,
        label: messages.common_reports,
        icon: GKIconName.PdfReport,
        notifications:
          (reportsNotificationsFlag && reportsNotifications) || undefined,
        appPath: "/reports",
        isEnabled: reportsFlag,
        qaId: "reports",
      },
      {
        useOperation: true,
        label: messages.router_carbon,
        icon: GKIconName.Carbon,
        appPath: "/carbon",
        isEnabled: carbonFlag,
        qaId: "carbon",
      },
      {
        useOperation: false,
        label: messages.router_fungicide_timing,
        icon: GKIconName.DateRange,
        appPath: "/spraytiming",
        isEnabled: canUseCropvisor,
        qaId: "cropvisor",
      },
      {
        useOperation: true,
        label: messages.router_corteva_labs,
        icon: GKIconName.Timeline,
        appPath: "/labs",
        isEnabled: labsFlag,
        qaId: "labs",
      },
    ],
    [
      messages,
      analysisFlag,
      analyzeFlag,
      activitiesFlag,
      carbonFlag,
      insightsNotifications,
      reportsFlag,
      reportsNotifications,
      reportsNotificationsFlag,
      seedAdvisorFlag,
      workspacesFlag,
      canUseCropvisor,
      labsFlag,
      nreFlag,
      getNreUrl,
    ],
  );

  const logAnalytics = React.useCallback((item: NavItem) => {
    // (Double check that we didn't get here without a key)
    if (item.analyticsKey) {
      item.analyticsMetadata
        ? segmentService.track(item.analyticsKey, item.analyticsMetadata)
        : segmentService.track(item.analyticsKey);
    }
  }, []);

  const memoizedResult = React.useMemo(
    () =>
      navItems
        .filter((item) => item.isEnabled)
        .map((item) => {
          const to = buildNavLink(item);
          return {
            label: item.label,
            to: item.href ? undefined : to,
            href: item.href,
            icon: item.icon,
            badge: item.notifications,
            badgeColor: item.notificationBadgeColor,
            "data-qa": `${primaryQaPrefix}--${item.qaId}`,
            onClick: item.customRoute
              ? (e) => {
                  if (item.analyticsKey) logAnalytics(item);
                  if (
                    (item.operationPath === "/fields" &&
                      /^\/app\/operations\/(.*)\/fields/.test(
                        window.location.pathname,
                      )) ||
                    ((item.operationPath === "/summary" ||
                      item.operationPath === "/insights") &&
                      /^\/app\/operations\/(.*)\/(summary|insights)/.test(
                        window.location.pathname,
                      ))
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    history.push(`${to}${window.location.search}`);
                  }
                }
              : item.analyticsKey
              ? () => logAnalytics(item)
              : undefined,
          } as FlexibleNavItem;
        }),
    [history, navItems, buildNavLink, logAnalytics],
  );
  return memoizedResult;
};

export const useSecondaryNavItems = (): FlexibleNavItem[] => {
  const locale = useLocale();
  const currentOperationId = useCurrentOperationId();
  const canViewOpSettings = selectCanViewOpSettings(useCurrentOperationData());
  const { logoutUrl } = useAuthenticationData();
  const messages = getCoreTranslationsFor(locale);
  const dataFlag = useFeatureFlag({
    app: "fabric",
    key: "core-nav-data",
  });
  const inviteNotificationFlag = useFeatureFlag({
    app: "fabric",
    key: "enable-farmer-invite-notification",
  });

  const { data: maybeUser } = useMaybeUser();
  const maybeMembersData = useMaybeMembersData();
  const currentUserIsCorteva = Boolean(
    maybeMembersData?.membersByGranularId[maybeUser?.granularId || ""]
      ?.cortevaType !== null,
  );
  const currentOpHasAdmin = React.useMemo(
    () => Boolean(maybeMembersData?.members.some((m) => m.permissions.isAdmin)),
    [maybeMembersData],
  );
  const showSettingsDot =
    inviteNotificationFlag && currentUserIsCorteva && !currentOpHasAdmin;

  const helpTo = React.useCallback((): string => {
    const baseUrl = "http://support.insights.granular.ag/hc";
    const region = getRegionFromUrl();
    if (region === "br") {
      // ignore the locale, there is only pt help in BR
      return `${baseUrl}/pt-br`;
    }
    if (region === "us") {
      // ignore the locale, there is only en help in US
      return `${baseUrl}/en-us`;
    }
    if (region === "ca") {
      if (locale.startsWith("fr")) {
        // If the language is french, send them fr-ca
        return `${baseUrl}/fr-ca`;
      } else {
        // Otherwise send them to english because there is no pt support
        return `${baseUrl}/en-ca`;
      }
    }
    // It will never reach this point but this is the ideal scenario.
    // We should have support for all regions across all languages.
    // Example (pt-ca, fr-br, fr-us, en-br) ands so on...
    return `${baseUrl}/${locale.toLowerCase().substring(0, 2)}-${region}`;
  }, [locale]);

  const memoizedResult = React.useMemo(
    () =>
      [
        dataFlag && {
          label: messages.Manage_Data,
          shortLabel: messages.router_data,
          to: "/data",
          icon: GKIconName.UsbStick,
          "data-qa": `${secondaryQaPrefix}--data`,
        },
        canViewOpSettings && {
          label: messages.Settings,
          shortLabel: messages.Settings,
          to: `/settings/operations/${currentOperationId}`,
          icon: GKIconName.Settings,
          badge: showSettingsDot ? "i" : undefined,
          "data-qa": `${secondaryQaPrefix}--operation`,
        },
        {
          label: messages.My_Profile,
          shortLabel: messages.common_profile,
          to: "/settings/account",
          icon: GKIconName.AccountBox,
          "data-qa": `${secondaryQaPrefix}--profile`,
        },
        {
          label: messages.common_help,
          href: helpTo(),
          icon: GKIconName.HelpOutline,
          "data-qa": `${secondaryQaPrefix}--help`,
        },
        {
          label: messages.common_logOut,
          href: logoutUrl,
          target: "_self",
          icon: GKIconName.ArrowBack,
          "data-qa": `${secondaryQaPrefix}--logout`,
          onClick: () => {
            localStorage.removeItem(LSRefreshKey);

            const { DD_RUM } = window as any;

            if (DD_RUM?.removeUser) {
              DD_RUM.removeUser();
            }
          },
        },
      ].filter((c) => c),
    [
      canViewOpSettings,
      currentOperationId,
      dataFlag,
      helpTo,
      messages,
      logoutUrl,
      showSettingsDot,
    ],
  );
  return memoizedResult;
};
